import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Brain, Users, Sparkles, ChevronDown, ChevronRight,
  Edit3, Save, Wand2, AlertCircle, Repeat
} from "lucide-react";
import api from "@/lib/api";
import { effectiveIsClinician } from "@/lib/effectiveClinician";
import { toast } from "sonner";

// ===== Labels =====
const ACTIVITY_LABELS = {
  lavoro: "Lavoro", studio: "Studio", riposo_relax: "Riposo / Relax",
  socialita: "Socialità", sport: "Sport", cura_personale: "Cura personale",
  commissioni: "Commissioni", altro: "Altro", pausa: "Pausa",
};
const LOAD_LABELS = { leggero: "leggero", medio: "medio", pesante: "pesante" };
const PLEAS_LABELS = { spiacevole: "spiacevole", neutra: "neutra", piacevole: "piacevole" };
const VOL_LABELS = { per_niente: "per niente scelto", abbastanza: "abbastanza scelto", totalmente: "totalmente scelto" };
const SOCIAL_CTX_LABELS = {
  solo: "solo/a", amici: "con amici", partner_famiglia: "con partner/famiglia",
  colleghi: "con colleghi", altri: "con altri", con_altri: "con altri",
};
const SOCIAL_QUAL_LABELS = {
  positiva: "interazione positiva", neutra: "interazione neutra",
  tesa: "interazione tesa", assente: "senza interazione",
};

const renderProfileLabel = (matrix, vars_) => {
  if (matrix === "A") {
    return [
      LOAD_LABELS[vars_.load] || vars_.load,
      PLEAS_LABELS[vars_.pleasantness] || vars_.pleasantness,
      VOL_LABELS[vars_.voluntariness] || vars_.voluntariness,
    ].join(" · ");
  }
  return [
    SOCIAL_CTX_LABELS[vars_.social_context] || vars_.social_context,
    SOCIAL_QUAL_LABELS[vars_.social_quality] || vars_.social_quality,
    PLEAS_LABELS[vars_.pleasantness] || vars_.pleasantness,
  ].join(" · ");
};

// ===== Single profile card =====
const ProfileCard = ({ profile, activity, matrix, isClinician = false, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [significato, setSignificato] = useState(profile.interpretation?.significato_possibile || "");
  const [insight, setInsight] = useState(profile.interpretation?.insight || "");

  useEffect(() => {
    setSignificato(profile.interpretation?.significato_possibile || "");
    setInsight(profile.interpretation?.insight || "");
  }, [profile.interpretation?.updated_at]);

  const hasInterp = !!profile.interpretation;
  const isUserEdited = profile.interpretation?.source === "user_edited";

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const res = await api.post("/voice-mood/profile-interpretation/draft", {
        activity,
        matrix,
        profile_key: profile.profile_key,
        variables: profile.variables,
        label_guess: profile.label_guess,
        count: profile.count,
      });
      setSignificato(res.data.significato_possibile || "");
      setInsight(res.data.insight || "");
      setEditing(true);
      toast.success("Bozza GPT-5.2 generata · ora rifinisci");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generazione fallita");
    } finally {
      setDrafting(false);
    }
  };

  const save = async (sourceTag) => {
    setSaving(true);
    try {
      await api.post("/voice-mood/profile-interpretation/save", {
        activity,
        matrix,
        profile_key: profile.profile_key,
        significato_possibile: significato.trim(),
        insight: insight.trim(),
        source: sourceTag,
      });
      toast.success("Salvato");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-md border ${profile.above_mean ? "border-indigo-300 bg-indigo-50/30" : "border-muted bg-white/60"}`}
      data-testid={`profile-card-${matrix}-${profile.profile_key}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-indigo-50/40 transition-colors"
        data-testid={`profile-toggle-${matrix}-${profile.profile_key}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                  : <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold">{profile.label_guess}</span>
            {profile.above_mean && (
              <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-indigo-400 text-indigo-700 bg-indigo-100">
                <Repeat className="w-2.5 h-2.5 mr-0.5" /> ricorrente
              </Badge>
            )}
            {hasInterp && isClinician && (
              <Badge variant="outline" className={`text-[9px] py-0 px-1.5 ${isUserEdited ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                {isUserEdited ? "rifinita" : "bozza GPT"}
              </Badge>
            )}
          </div>
          {/* 2026-06 (Laura): variabili tecniche (load · pleasantness · social_quality)
              visibili SOLO al clinico. Per le beta tester il nome del pattern basta. */}
          {isClinician && (
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
              {renderProfileLabel(matrix, profile.variables)}
            </p>
          )}
        </div>
        {/* 2026-06: Badge "×N" tecnico → solo clinico.
            Per le beta tester il badge "ricorrente" basta. */}
        {isClinician && (
          <Badge variant="secondary" className="text-[10px] tabular-nums shrink-0 ml-1">
            ×{profile.count}
          </Badge>
        )}
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 pt-1 space-y-2.5 border-t border-muted/60">
          {/* Variabili readonly — clinician only (hides the composition rule for beta testers) */}
          {isClinician && (
            <div className="text-[10px] text-muted-foreground bg-muted/40 rounded px-2 py-1">
              <strong>Variabili:</strong> {renderProfileLabel(matrix, profile.variables)} · {profile.count} occorrenze
            </div>
          )}

          {/* Interpretation */}
          {!hasInterp && !editing ? (
            isClinician ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generateDraft}
                  disabled={drafting}
                  className="text-xs h-8"
                  data-testid={`profile-draft-${matrix}-${profile.profile_key}`}
                >
                  {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  Bozza GPT-5.2
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(true)}
                  className="text-xs h-8"
                  data-testid={`profile-write-${matrix}-${profile.profile_key}`}
                >
                  <Edit3 className="w-3.5 h-3.5 mr-1" /> Scrivi tu
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground italic" data-testid={`profile-no-interp-${matrix}-${profile.profile_key}`}>
                Lettura in preparazione — questo profilo si sta rivelando, dacci ancora qualche check-in.
              </p>
            )
          ) : (
            <>
              <div>
                <label className="text-[11px] font-semibold text-indigo-900 block mb-1">
                  {isClinician ? "Significato possibile" : "Cosa potrebbe voler dire"}
                </label>
                {editing && isClinician ? (
                  <Textarea
                    value={significato}
                    onChange={(e) => setSignificato(e.target.value)}
                    rows={4}
                    className="text-xs resize-y"
                    placeholder="Interpretazione clinica (citando il framework usato)…"
                    data-testid={`profile-significato-${matrix}-${profile.profile_key}`}
                  />
                ) : (
                  <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed bg-white/80 rounded p-2 border border-muted">
                    {significato || <em className="text-muted-foreground">(vuoto)</em>}
                  </p>
                )}
              </div>
              {(isClinician || insight) && (
                <div>
                  <label className="text-[11px] font-semibold text-indigo-900 block mb-1">
                    {isClinician ? "Insight" : "Spunto"}
                  </label>
                  {editing && isClinician ? (
                    <Textarea
                      value={insight}
                      onChange={(e) => setInsight(e.target.value)}
                      rows={2}
                      className="text-xs resize-y"
                      placeholder="Cosa esplorare nelle prossime occorrenze…"
                      data-testid={`profile-insight-${matrix}-${profile.profile_key}`}
                    />
                  ) : (
                    <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed bg-white/80 rounded p-2 border border-muted">
                      {insight || <em className="text-muted-foreground">(vuoto)</em>}
                    </p>
                  )}
                </div>
              )}

              {/* 2026-05-30 — Scientific references shown AFTER the body text,
                  as clickable links. Generated by GPT in `riferimenti_scientifici[]`
                  by the new prompt. Hidden if the saved draft is pre-2026-05-30
                  (no references field). */}
              {Array.isArray(profile.interpretation?.riferimenti_scientifici)
                && profile.interpretation.riferimenti_scientifici.length > 0 && (
                <div data-testid={`profile-refs-${matrix}-${profile.profile_key}`}>
                  <label className="text-[10px] font-semibold text-indigo-900/70 block mb-1 uppercase tracking-wider">
                    Riferimenti scientifici
                  </label>
                  <ul className="text-[11px] space-y-0.5 bg-white/60 rounded p-2 border border-muted/60">
                    {profile.interpretation.riferimenti_scientifici.map((ref, idx) => (
                      <li key={idx} className="leading-snug">
                        {ref.url ? (
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-700 hover:text-indigo-900 underline decoration-dotted"
                          >
                            {ref.autore}{ref.titolo ? ` — ${ref.titolo}` : ""}
                          </a>
                        ) : (
                          <span>{ref.autore}{ref.titolo ? ` — ${ref.titolo}` : ""}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Clinician-only edit controls */}
              {isClinician && (
              <div className="flex items-center gap-2 pt-1">
                {editing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => save("user_edited")}
                      disabled={saving || (!significato.trim() && !insight.trim())}
                      className="text-xs h-8"
                      data-testid={`profile-save-${matrix}-${profile.profile_key}`}
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Salva
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(false);
                        setSignificato(profile.interpretation?.significato_possibile || "");
                        setInsight(profile.interpretation?.insight || "");
                      }}
                      disabled={saving}
                      className="text-xs h-8"
                    >
                      Annulla
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(true)}
                      className="text-xs h-8"
                      data-testid={`profile-edit-${matrix}-${profile.profile_key}`}
                    >
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Rifinisci
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={generateDraft}
                      disabled={drafting}
                      className="text-xs h-8"
                    >
                      {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                      Rigenera bozza
                    </Button>
                  </>
                )}
              </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ===== Matrix block (A or B) =====
const MatrixBlock = ({ activity, matrix, matrixData, icon, title, subtitle, isClinician = false, onSaved }) => {
  const allProfiles = matrixData?.profiles || [];
  // 2026-05-21: when the server strips `above_mean` (non-clinician view), every
  // profile is treated as "shown" — partitioning by computation rules is hidden.
  const hasRuleInfo = allProfiles.some((p) => "above_mean" in p);
  const shown = hasRuleInfo ? allProfiles.filter((p) => p.above_mean) : allProfiles;
  const hidden = hasRuleInfo ? allProfiles.filter((p) => !p.above_mean) : [];
  const [showAll, setShowAll] = useState(false);

  if (allProfiles.length === 0) {
    return (
      <Card className="border" data-testid={`matrix-${matrix}-${activity}`}>
        <CardHeader className="pb-1.5 px-3 pt-3">
          <CardTitle className="text-xs flex items-center gap-1.5">
            {icon} {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <p className="text-[11px] text-muted-foreground italic">Nessun profilo nel dataset.</p>
        </CardContent>
      </Card>
    );
  }

  const list = showAll ? allProfiles : (shown.length > 0 ? shown : allProfiles);

  return (
    <Card className="border" data-testid={`matrix-${matrix}-${activity}`}>
      <CardHeader className="pb-1.5 px-3 pt-3">
        <CardTitle className="text-xs flex items-center gap-1.5">
          {icon} {title}
        </CardTitle>
        <p className="text-[10px] text-muted-foreground leading-tight">{subtitle}</p>
        {/* 2026-06 (Laura): metadati tecnici (occorrenze · profili distinti · media)
            visibili SOLO al clinico. */}
        {isClinician && (
          <p className="text-[10px] text-muted-foreground">
            {matrixData.total_in_activity} occorrenze · {matrixData.unique_profiles} profili distinti
            {hasRuleInfo && matrixData.mean_count !== undefined ? ` · media ${matrixData.mean_count}` : ""}
          </p>
        )}
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2">
        {hasRuleInfo && shown.length === 0 && !showAll && isClinician && (
          <div className="flex items-start gap-1.5 p-2 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-900">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Nessun profilo sopra la media (tutti N=1). Servono più dati per identificare pattern ricorrenti.</span>
          </div>
        )}
        {list.map((p) => (
          <ProfileCard
            key={p.profile_key}
            profile={p}
            activity={activity}
            matrix={matrix}
            isClinician={isClinician}
            onSaved={onSaved}
          />
        ))}
        {/* Toggle "profili isolati" → solo clinico (gli altri vedono solo i ricorrenti) */}
        {hidden.length > 0 && isClinician && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline"
            data-testid={`matrix-${matrix}-${activity}-toggle-hidden`}
          >
            {showAll ? "↑ nascondi profili isolati" : `↓ mostra anche profili isolati (${hidden.length})`}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

// ===== Main panel =====
const ProfileMatrixSection = ({ includeV1 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClinician, setIsClinician] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/voice-mood/profile-matrix?include_v1=${includeV1}&days=365`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento matrice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const refresh = () => api.get("/auth/me")
      .then((r) => setIsClinician(effectiveIsClinician(r.data?.is_clinician)))
      .catch(() => {});
    refresh();
    const onChange = () => refresh();
    window.addEventListener("revela:forced-user-view-changed", onChange);
    return () => window.removeEventListener("revela:forced-user-view-changed", onChange);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeV1]);

  if (loading) {
    return (
      <Card className="border">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.activities.length === 0) {
    return (
      <Card className="border-dashed" data-testid="profile-matrix-empty">
        <CardContent className="py-6 text-center">
          <Brain className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs font-medium">Matrice profili vuota</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Servono check-in con attività, dimensioni EMA e contesto sociale per popolare i profili.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="profile-matrix-section">
      <Card className="border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-purple-50/40">
        <CardContent className="p-3">
          <h3
            className="text-base sm:text-lg font-semibold text-indigo-900 flex items-center gap-1.5"
            style={!isClinician ? { fontFamily: "'Cormorant Garamond', serif" } : undefined}
          >
            <Sparkles className="w-4 h-4" /> {isClinician ? "Matrice dei profili esperienziali" : "I tuoi modi di vivere le situazioni"}
          </h3>
          {isClinician ? (
            <>
              <p className="text-[11px] text-indigo-800/80 mt-0.5 leading-relaxed">
                Person-centered analysis: per ogni attività, identifico le <strong>configurazioni
                relazionali ricorrenti</strong> (count &gt; media per attività) sull'asse
                contesto × qualità × piacevolezza. La bozza GPT-5.2 è in linguaggio piano
                (niente variabili nominate, niente framework citati nel testo). I riferimenti
                scientifici compaiono alla fine, con link cliccabili.
              </p>
            </>
          ) : (
            <p className="text-xs text-indigo-800/80 mt-1 leading-relaxed">
              Per ogni tipo di attività che fai (lavoro, vita sociale, riposo…) qui sotto
              trovi i <strong>tuoi modi più frequenti di viverla</strong>, con una breve
              ipotesi di lettura. Sono piccole fotografie ricorrenti dei tuoi stati: ti
              aiutano a riconoscere cosa si ripete e dove c'è spazio per esplorare.
            </p>
          )}
        </CardContent>
      </Card>

      {data.activities.map((act) => (
        <Card
          key={act.activity}
          className="border border-indigo-200/50"
          data-testid={`activity-block-${act.activity}`}
        >
          <CardHeader className="pb-1.5 px-3 pt-3 bg-indigo-50/40 border-b border-indigo-200/50">
            <CardTitle className="text-sm font-semibold text-indigo-900 uppercase tracking-wide">
              {ACTIVITY_LABELS[act.activity] || act.activity}
              {/* 2026-06 (Laura): "X occorrenze totali" tecnico → solo clinico */}
              {isClinician && (
                <span className="ml-2 text-[10px] font-normal text-indigo-700/80">
                  {act.total_entries} occorrenze totali
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <MatrixBlock
              activity={act.activity}
              matrix="B"
              matrixData={act.matrix_b}
              icon={<Users className="w-3.5 h-3.5 text-teal-600" />}
              title={isClinician ? "Matrice B · Esperienza relazionale" : "Come vivi gli altri in questa attività"}
              subtitle={isClinician ? "contesto sociale × qualità sociale × piacevolezza" : "i modi più frequenti con cui vivi questi momenti"}
              isClinician={isClinician}
              onSaved={load}
            />
            {/* 2026-05-30 — Matrice A removed from UI (Laura request):
                redundant with the 60 clinical patterns (work × 4 social contexts × 12 configs).
                Backend still computes it for legacy compatibility but UI shows only Matrice B. */}          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ProfileMatrixSection;
